import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { ArrowPathIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { PauseIcon, PlayIcon, CheckIcon } from '@heroicons/react/24/solid';

interface Slide {
  id: string;
  label: string;
  icon: string;
}

interface TournamentOption {
  id: string;
  name: string;
}

interface Props {
  slides: Slide[];
  currentSlide: number;
  lastUpdated: Date | null;
  tournamentName: string | null;
  tournaments: TournamentOption[];
  selectedTournamentId: string | null;
  onTournamentSelect: (id: string) => void;
  paused: boolean;
  onSlideSelect: (index: number) => void;
  onPauseToggle: () => void;
}

export default function TvStatusBar({
  slides,
  currentSlide,
  lastUpdated,
  tournamentName,
  tournaments,
  selectedTournamentId,
  onTournamentSelect,
  paused,
  onSlideSelect,
  onPauseToggle,
}: Props) {
  const [time, setTime] = useState(new Date());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="flex items-center justify-between px-3 sm:px-6 lg:px-10 xl:px-14 py-2 sm:py-3 lg:py-4 bg-black/60 backdrop-blur-sm border-t border-white/10 text-white/70 select-none gap-2 flex-wrap sm:flex-nowrap flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0" ref={dropdownRef}>
        <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 rounded-full overflow-hidden bg-white flex items-center justify-center flex-shrink-0 p-0.5">
          <img src="https://wjwcuakqeesxnbjznajh.supabase.co/storage/v1/object/public/images/ibomopen2026.png" alt="Ibom Open" className="w-full h-full object-contain" />
        </div>
        {tournaments.length > 1 ? (
          <div className="relative">
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-1 text-white/50 text-xs sm:text-sm lg:text-base font-medium hover:text-white/80 transition-colors"
            >
              <span className="whitespace-nowrap">{tournamentName ?? 'Select Tournament'}</span>
              <ChevronUpDownIcon className="w-3.5 h-3.5 flex-shrink-0 text-white/30" />
            </button>
            {dropdownOpen && (
              <div className="absolute bottom-full mb-2 left-0 w-60 sm:w-72 bg-black/90 backdrop-blur-md border border-white/10 rounded-xl shadow-xl overflow-hidden z-50">
                <div className="px-3 py-2 border-b border-white/10">
                  <p className="text-white/40 text-[10px] uppercase tracking-widest font-semibold">Select Tournament</p>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {tournaments.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { onTournamentSelect(t.id); setDropdownOpen(false); }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-white/10 transition-colors"
                    >
                      <span className={`text-xs sm:text-sm truncate ${t.id === selectedTournamentId ? 'text-golf-orange font-semibold' : 'text-white/70'}`}>
                        {t.name}
                      </span>
                      {t.id === selectedTournamentId && (
                        <CheckIcon className="w-3.5 h-3.5 text-golf-orange flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className="text-white/50 text-xs sm:text-sm lg:text-base font-medium truncate max-w-[100px] sm:max-w-[180px] lg:max-w-none">
            {tournamentName ?? 'Ibom Open Golf Tournament'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
        <button
          onClick={onPauseToggle}
          title={paused ? 'Resume slideshow (Space)' : 'Pause slideshow (Space)'}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-full text-[10px] sm:text-xs lg:text-sm font-semibold transition-all border ${
            paused
              ? 'bg-golf-orange text-white border-golf-orange shadow shadow-golf-orange/30'
              : 'bg-white/10 text-white/60 border-white/10 hover:bg-white/20 hover:text-white'
          }`}
        >
          {paused ? (
            <PlayIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
          ) : (
            <PauseIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
          )}
        </button>

        <div className="w-px h-4 sm:h-5 lg:h-6 bg-white/10" />

        {slides.map((slide, i) => (
          <button
            key={slide.id}
            onClick={() => onSlideSelect(i)}
            className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-full text-[10px] sm:text-xs lg:text-sm font-medium transition-all ${
              i === currentSlide
                ? 'bg-golf-orange text-white shadow'
                : 'bg-white/10 text-white/50 hover:bg-white/20'
            }`}
          >
            <span>{slide.icon}</span>
            <span className="hidden sm:inline">{slide.label}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 sm:gap-4 lg:gap-5">
        {lastUpdated && (
          <span className="flex items-center gap-1 text-white/30 text-[10px] sm:text-xs lg:text-sm">
            <ArrowPathIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-3.5 lg:h-3.5" />
            {format(lastUpdated, 'HH:mm')}
          </span>
        )}
        <span className="text-white font-semibold text-base sm:text-lg lg:text-xl xl:text-2xl font-display tabular-nums">
          {format(time, 'HH:mm:ss')}
        </span>
        <span className="text-white/50 text-xs sm:text-sm lg:text-base hidden sm:inline">
          {format(time, 'EEE, MMM d')}
        </span>
      </div>
    </div>
  );
}
